import { ChangeDetectionStrategy, Component, ElementRef, inject, input, OnDestroy, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { DfEventService } from '@df/core/service/df-event';
import { UiComponent } from '@df/ui/ui.component';
import { ScrollService } from '@df/util/service/scroll.service';
import { DIALOG_EVENT, DialogEvent, DialogEventType, type DialogPosition } from './dialog.interface';

@Component({
    selector: 'dialog',
    exportAs: 'dialog',
    templateUrl: './dialog.component.html',
    styleUrl: './dialog.component.scss',
    host: {
        '[class._left-side]': 'side() === "left"',
        '[class._right-side]': 'side() === "right"',
        '[class._center-side]': 'side() === "center"',
        '[class._is-cms-open]': 'this.client.isCms'
    },
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DialogComponent extends UiComponent implements OnDestroy {
    id = input.required<string>();
    side = input<DialogPosition>('left');
    blockScroll = input(true);
    smallText = input(false);
    scrollId = input<string>();

    isVisible = signal(false);
    isVisible$ = toObservable(this.isVisible);

    private scrollRelease?: () => void;

    private readonly elementRef = inject(ElementRef<HTMLDialogElement>);
    private readonly dfEventService = inject(DfEventService);
    private readonly scrollService = inject(ScrollService);

    constructor() {
        super();

        this.subscriptions.add(
            this.dfEventService.success(event => {
                if (event.id === DIALOG_EVENT) {
                    const dialogEvent = event.data as DialogEvent;
                    if (dialogEvent.id === this.id()) {
                        if (dialogEvent.type === DialogEventType.Open) {
                            this.showModal();
                        } else {
                            this.close();
                        }
                    }
                }
            })
        );

        this.element.addEventListener('click', this.backdropClick.bind(this));
    }

    get element(): HTMLDialogElement {
        return this.elementRef.nativeElement;
    }

    override ngOnDestroy() {
        this.releaseScroll();

        this.element.removeEventListener('click', this.backdropClick.bind(this));

        super.ngOnDestroy();
    }

    close(): void {
        if (this.isVisible()) {
            console.log('close');
            this.element.close();

            this.isVisible.set(false);

            this.releaseScroll();
        }
    }

    showModal(): void {
        if (!this.isVisible()) {
            if(this.client.isCms) {
                this.element.show();
            } else {
                this.element.showModal();
            }

            this.isVisible.set(true);

            if (this.blockScroll()) {
                this.scrollRelease = this.scrollService.blockScroll();
            }
        }
    }

    protected backdropClick(event: MouseEvent): void {
        if (!this.isInDialog(event)) {
            this.close();
        }
    }

    protected isInDialog(event: MouseEvent): boolean {
        const rect = this.element.getBoundingClientRect();
        return event.clientX > rect.left && event.clientX < rect.right &&
               event.clientY > rect.top && event.clientY < rect.bottom;
    }

    private releaseScroll(): void {
        if (this.scrollRelease) {
            this.scrollRelease();
            this.scrollRelease = undefined;
        }
    }
}
